import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    Card,
    Col,
    Container,
    FormControl,
    Row
} from "react-bootstrap";
import {
    ImportProjectState,
    DeveloperProjectVariables,
    NewProjectConfigData,
    Organization,
    Product,
    ImportProjectData
} from "../../models/types";
import { useMutation, useQuery } from "@apollo/client";
import { IMPORT_PROJECT } from "../../graphql/mutations";
import { GET_CREATE_PROJECT_CONFIG } from "../../graphql/queries";
import { OrganizationSelectionView } from "../views/OrganizationSelectionView";
import { ProductSelectionView } from "../views/ProductSelectionView";

export const DeveloperImportProjectPage = () => {
    const errorReference = useRef(null);
    const [base64String, setBase64String] = useState("");
    const [result, updateResult] = useState<string[]>([]);
    const [state, updateState] = useState<ImportProjectState>(
        ImportProjectState.Organization
    );
    const [organization, updateOrganization] = useState<
        Organization | undefined
    >(undefined);
    const [product, updateProduct] = useState<Product | undefined>(undefined);
    const [showError, updateShowError] = useState(false);
    const { data: { getCreateProjectConfig } = {} } =
        useQuery<NewProjectConfigData>(GET_CREATE_PROJECT_CONFIG);

    const [importProjectMutation, { error }] = useMutation<
        ImportProjectData,
        DeveloperProjectVariables
    >(IMPORT_PROJECT);

    useEffect(() => {
        if (organization) {
            updateProduct(undefined);
            updateState(ImportProjectState.Product);
        }
    }, [organization]);

    useEffect(() => {
        if (product) {
            updateState(ImportProjectState.Validate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product]);

    useEffect(() => {
        let timer: any | undefined = undefined;
        if (error) {
            updateShowError(true);
            timer = setTimeout(() => {
                updateShowError(false);
            }, 20000);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [error]);

    useEffect(() => {
        if (showError) {
            // @ts-ignore
            errorReference.current?.scrollIntoView();
        }
    }, [showError]);

    const importProject = async () => {
        if (
            product === undefined ||
            base64String === undefined ||
            base64String === ""
        ) {
            console.log("[DEBUG] no string fool");
            return;
        }
        const result = await importProjectMutation({
            variables: {
                productId: Number(product.id),
                config: base64String
            }
        });
        console.log("[DEBUG] result ", result);
        console.log("[DEBUG] result.data ", result.data);
        console.log(
            "[DEBUG] result.data?.importProject ",
            result.data?.importProject
        );
        if (result.data?.importProject) {
            console.log("[DEBUG] done");
            updateState(ImportProjectState.Completed);
            updateResult(result.data?.importProject.manualActions);
        } else {
            console.log("[DEBUG] failed");
            updateState(ImportProjectState.Failed);
        }
        setBase64String("");
    };

    const downloadInstuctions = async () => {
        if (!result || result.length === 0) {
            return;
        }
        const blob = new Blob(result, { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "instructions.txt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const clear = () => {
        setBase64String("");
        updateResult([]);
        updateOrganization(undefined);
        updateProduct(undefined);
        updateShowError(false);
        updateState(ImportProjectState.Organization);
    };

    if (!getCreateProjectConfig) {
        return (
            <Container className="fullSize p-2 pt-5 d-flex justify-content-center text-contrast">
                <p>Loading Configuration...</p>
            </Container>
        );
    }

    console.log("[DEBUG] result ", result);

    return (
        <Container className="fullSize p-2 pt-4">
            <Row className="d-flex justify-content-center align-items-center m-2">
                <Col xs="auto">
                    <h4 className="text-muted">Import Project</h4>
                </Col>
            </Row>
            <Row className="text-contrast m-0 p-0 d-flex justify-content-center align-items-center">
                <Col xs="auto">
                    <div className="p-3 no-border">
                        {state < ImportProjectState.Completed ? (
                            <OrganizationSelectionView
                                organization={organization}
                                updateOrganization={updateOrganization}
                            />
                        ) : null}
                        {state >= ImportProjectState.Organization &&
                        organization &&
                        state < ImportProjectState.Completed ? (
                            <ProductSelectionView
                                organization={organization}
                                product={product}
                                updateProduct={updateProduct}
                            />
                        ) : null}
                        {state === ImportProjectState.Validate ? (
                            <Card className="d-flex justify-content-center text-contrast">
                                <Card.Header>
                                    <h5>Paste export</h5>
                                </Card.Header>
                                <Card.Body>
                                    <FormControl
                                        as="textarea"
                                        style={{
                                            minWidth: "500px",
                                            minHeight: "300px"
                                        }}
                                        type="string"
                                        placeholder="Add value"
                                        value={base64String}
                                        onChange={e =>
                                            setBase64String(e.target.value)
                                        }
                                    />
                                </Card.Body>
                                <Card.Footer className="d-flex justify-content-center">
                                    <Button onClick={importProject}>
                                        Import
                                    </Button>
                                </Card.Footer>
                            </Card>
                        ) : null}
                        {state === ImportProjectState.Completed ? (
                            <Card className="d-flex justify-content-center text-contrast">
                                <Card.Header>
                                    <h5>Import Completed Successfully</h5>
                                </Card.Header>
                                <Card.Body>
                                    {result.length > 0 ? (
                                        <code style={{ whiteSpace: "pre" }}>
                                            {result}
                                        </code>
                                    ) : (
                                        <p className="success">
                                            No manual actions required
                                        </p>
                                    )}
                                </Card.Body>
                                <Card.Footer className="d-flex justify-content-center">
                                    <Button
                                        variant="success me-1"
                                        onClick={downloadInstuctions}
                                    >
                                        Download Instructions
                                    </Button>
                                    <Button variant="info ms-1" onClick={clear}>
                                        Import Another
                                    </Button>
                                </Card.Footer>
                            </Card>
                        ) : null}
                        {error && showError ? (
                            <Card
                                ref={errorReference}
                                className="text-danger border-danger"
                            >
                                <Card.Header>Error</Card.Header>
                                <Card.Body>
                                    <span>{error.message}</span>
                                </Card.Body>
                                <Card.Footer className="d-flex justify-content-center">
                                    <Button variant="info ms-1" onClick={clear}>
                                        Retry
                                    </Button>
                                </Card.Footer>
                            </Card>
                        ) : null}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};
